import React, { useRef, useState } from 'react'

// Drop or pick key and certificate files instead of opening them and pasting.
// Everything happens in the browser: files are read into the form's fields
// and only leave with the form, on Save, exactly as pasted text would.

const PEM_BLOCK = /-----BEGIN ([A-Z0-9 ]+)-----[\s\S]+?-----END \1-----/g

// The PEM blocks in a text, sorted into keys and certificates by their own
// headers (never by file name).
export const pemBlocks = text => {
  const found = { keys: [], certificates: [] }
  for (const match of String(text).matchAll(PEM_BLOCK)) {
    if (/PRIVATE KEY$/.test(match[1])) found.keys.push(match[0])
    else if (match[1] === 'CERTIFICATE') found.certificates.push(match[0])
  }
  return found
}

const toBase64 = bytes => {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return window.btoa(binary)
}

// A binary (DER) certificate, as Windows and Entra often export .cer files,
// wrapped as PEM text.
export const derToPem = bytes => {
  const body = toBase64(bytes).match(/.{1,64}/g).join('\n')
  return `-----BEGIN CERTIFICATE-----\n${body}\n-----END CERTIFICATE-----`
}

const pemBytes = pem => {
  const binary = window.atob(pem.replace(/-----[^-]+-----/g, '').replace(/\s+/g, ''))
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

// One DER tag-length-value at offset: its tag and where its content starts
// and ends.
const tlv = (bytes, offset) => {
  const tag = bytes[offset]
  let length = bytes[offset + 1]
  let start = offset + 2
  if (length & 0x80) {
    const count = length & 0x7f
    length = 0
    for (let i = 0; i < count; i++) length = (length << 8) | bytes[start + i]
    start += count
  }
  return { tag, start, end: start + length }
}

const children = (bytes, node) => {
  const list = []
  for (let offset = node.start; offset < node.end;) {
    const child = tlv(bytes, offset)
    list.push(child)
    offset = child.end
  }
  return list
}

// UTCTime YYMMDDHHMMSSZ (years 50-99 are 19xx) or GeneralizedTime
// YYYYMMDDHHMMSSZ.
const parseTime = (bytes, node) => {
  const text = String.fromCharCode(...bytes.slice(node.start, node.end))
  const full = node.tag === 0x17 ? `${Number(text.slice(0, 2)) < 50 ? '20' : '19'}${text}` : text
  const [y, mo, d, h, mi, s] = [0, 4, 6, 8, 10, 12].map((at, i) => Number(full.slice(at, at + (i === 0 ? 4 : 2))))
  return new Date(Date.UTC(y, mo - 1, d, h, mi, s))
}

// The certificate's own expiry (validity.notAfter), or null when the text
// isn't a certificate this can read.
export const certificateNotAfter = pem => {
  try {
    const bytes = pemBytes(pem)
    const tbs = children(bytes, tlv(bytes, 0))[0]
    const fields = children(bytes, tbs)
    // tbsCertificate: [0] version (optional), serial, signature, issuer, validity
    const validity = fields[fields[0].tag === 0xa0 ? 4 : 3]
    const [, notAfter] = children(bytes, validity)
    const date = parseTime(bytes, notAfter)
    return Number.isNaN(date.getTime()) ? null : date
  } catch (e) {
    return null
  }
}

const readFile = file => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onload = () => resolve(new Uint8Array(reader.result))
  reader.onerror = () => reject(reader.error)
  reader.readAsArrayBuffer(file)
})

// Reads each file as PEM text, or as a DER certificate when it is binary.
export const readCredentialFiles = async files => {
  const found = { keys: [], certificates: [], unreadable: [] }
  for (const file of files) {
    const bytes = await readFile(file)
    // PEM is ASCII, so bytes map straight to characters.
    let text = ''
    for (let i = 0; i < bytes.length; i++) text += String.fromCharCode(bytes[i])
    const blocks = pemBlocks(text)
    if (blocks.keys.length || blocks.certificates.length) {
      found.keys.push(...blocks.keys)
      found.certificates.push(...blocks.certificates)
    } else if (bytes[0] === 0x30 && certificateNotAfter(derToPem(bytes))) {
      found.certificates.push(derToPem(bytes))
    } else {
      found.unreadable.push(file.name)
    }
  }
  return found
}

const describe = (found, expires) => {
  const parts = []
  if (found.keys.length) parts.push('private key')
  if (found.certificates.length) parts.push(`certificate${expires ? ` (expires ${expires.toLocaleDateString()})` : ''}`)
  const filled = parts.length ? `Filled in the ${parts.join(' and the ')}.` : ''
  const skipped = found.unreadable.length ? ` No key or certificate in ${found.unreadable.join(', ')}.` : ''
  return (filled + skipped).trim()
}

// onKey(pem), onCertificate(pem, notAfter): what to do with each kind found.
export const PemDrop = ({ onKey, onCertificate, disabled }) => {
  const input = useRef(null)
  const [over, setOver] = useState(false)
  const [message, setMessage] = useState(null)

  const take = async fileList => {
    const files = Array.from(fileList || [])
    if (!files.length || disabled) return
    const found = await readCredentialFiles(files)
    const expires = found.certificates.length ? certificateNotAfter(found.certificates[0]) : null
    if (found.keys.length) onKey(found.keys[0])
    if (found.certificates.length) onCertificate(found.certificates[0], expires)
    setMessage(describe(found, expires))
  }

  return (
    <div className={`sw-drop${over ? ' sw-drop-over' : ''}`} data-testid="sw-pem-drop"
      onDragOver={e => { e.preventDefault(); if (!disabled) setOver(true) }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); take(e.dataTransfer.files) }}>
      <span>Drop the key and certificate files here, or</span>
      <button type="button" className="btn sw-btn btn-sm" disabled={disabled} onClick={() => input.current && input.current.click()}>Choose files…</button>
      <input ref={input} type="file" multiple hidden accept=".pem,.key,.crt,.cer,.txt" aria-label="key and certificate files"
        onChange={e => { take(e.target.files); e.target.value = '' }} />
      {message && <span className="sw-hint w-100" role="status">{message}</span>}
    </div>
  )
}
